// frontend/src/pages/EarningsPage.tsx
//
// Creator earnings dashboard showing lifetime stats, current month progress,
// historical earnings, and payout history.
// For free users, shows a prompt to upgrade to a paid plan.

import { useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useCreatorEarnings, formatMonth, getStatusColor, formatStatus } from '../hooks/useCreatorEarnings';
import { useCreatorConnect } from '../hooks/useCreatorConnect';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';
import { 
  DollarSign, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ExternalLink,
  RefreshCw,
  ArrowLeft,
  Zap,
  Calendar,
  Lock,
  Sparkles,
  ArrowRight
} from 'lucide-react';

// Component shown to free users explaining they need to upgrade
function FreeUserPrompt() {
  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-6">
            <Lock className="h-10 w-10 text-accent-500" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-theme-text mb-4">
            Creator Earnings
          </h1>
          <p className="text-lg text-theme-text-secondary">
            Earn money when paid users run your mini-apps
          </p>
        </div>

        <div className="card p-8 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-lg bg-amber-500/10">
              <Sparkles className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <h2 className="font-semibold text-theme-text text-lg mb-2">
                Upgrade to Start Earning
              </h2>
              <p className="text-theme-text-secondary">
                Creator earnings are available exclusively to users on a paid subscription plan. 
                When you upgrade, you'll be able to earn <span className="text-accent-500 font-medium">70% of subscription revenue</span> based 
                on how often other paid users run your published mini-apps.
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <h3 className="font-medium text-theme-text">How it works:</h3>
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-500/10 flex items-center justify-center text-accent-500 font-semibold text-sm">
                  1
                </div>
                <div>
                  <p className="font-medium text-theme-text">Create & publish mini-apps</p>
                  <p className="text-sm text-theme-text-secondary">
                    Build useful mini-apps and make them public for others to discover
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-500/10 flex items-center justify-center text-accent-500 font-semibold text-sm">
                  2
                </div>
                <div>
                  <p className="font-medium text-theme-text">Paid users run your apps</p>
                  <p className="text-sm text-theme-text-secondary">
                    Every time a paid subscriber uses your app, it counts toward your share
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-500/10 flex items-center justify-center text-accent-500 font-semibold text-sm">
                  3
                </div>
                <div>
                  <p className="font-medium text-theme-text">Get paid monthly</p>
                  <p className="text-sm text-theme-text-secondary">
                    70% of subscription revenue is distributed among creators based on usage
                  </p>
                </div>
              </div>
            </div>
          </div>

          {PAYMENTS_DISABLED ? (
            <div className="p-3 rounded-lg bg-theme-surface border border-theme-border-muted text-sm text-theme-text-secondary text-center">
              {PAYMENTS_DISABLED_MESSAGE}
            </div>
          ) : (
            <Link 
              to="/pricing" 
              className="btn btn-primary btn-block text-base py-3"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              View Subscription Plans
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
          )}
        </div>

        <div className="text-center">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-sm text-theme-text-secondary hover:text-theme-text transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export function EarningsPage() {
  const { profile, isPaidUser, creatorFeaturesEnabled } = useAuth();
  const { data, loading, error, refetch } = useCreatorEarnings();
  const { loading: connectLoading, startOnboarding, openDashboard } = useCreatorConnect();

  useEffect(() => {
    document.title = 'Earnings - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  // If creator features are disabled by admin, redirect to dashboard
  if (!creatorFeaturesEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show upgrade prompt for free users
  if (!isPaidUser) {
    return <FreeUserPrompt />;
  }

  const isConnected = profile?.stripe_onboarding_completed ?? false;
  const isChargesEnabled = profile?.stripe_charges_enabled ?? false;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="p-6 rounded-xl bg-theme-error-bg border border-theme-error-border">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="h-5 w-5 text-theme-error" />
              <h2 className="font-semibold text-theme-error">Error loading earnings</h2>
            </div>
            <p className="text-sm text-theme-text-secondary mb-4">{error}</p>
            <button onClick={refetch} className="btn btn-secondary">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-sm text-theme-text-secondary hover:text-theme-text mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-theme-text">Creator Earnings</h1>
              <p className="text-theme-text-secondary mt-1">
                Track your earnings from mini-app usage
              </p>
            </div>
            <button 
              onClick={refetch} 
              className="btn btn-ghost"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Stripe Connect Status */}
        {!isConnected && (
          <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-accent-500/10 to-cyan-500/10 border border-accent-500/20">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-accent-500/20">
                <DollarSign className="h-6 w-6 text-accent-500" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-theme-text mb-1">Connect Stripe to receive payouts</h2>
                <p className="text-sm text-theme-text-secondary mb-4">
                  Set up your payout account to start receiving your share of subscription revenue.
                  You'll earn 70% of revenue based on how often your apps are used.
                </p>
                <button
                  onClick={() => startOnboarding()}
                  disabled={connectLoading}
                  className="btn btn-primary"
                >
                  {connectLoading ? <LoadingSpinner size="sm" /> : (
                    <>
                      <DollarSign className="h-4 w-4 mr-2" />
                      Connect Stripe
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {isConnected && !isChargesEnabled && (
          <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-amber-500/20">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-theme-text mb-1">Stripe connected — verification pending</h2>
                <p className="text-sm text-theme-text-secondary mb-4">
                  Your Stripe account is connected but requires additional verification before payouts can be processed.
                  Please check your Stripe dashboard to complete any outstanding requirements.
                </p>
                <button
                  onClick={() => openDashboard()}
                  disabled={connectLoading}
                  className="btn btn-secondary"
                >
                  {connectLoading ? <LoadingSpinner size="sm" /> : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Stripe Dashboard
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Lifetime Earnings */}
          <div className="p-5 rounded-xl bg-theme-surface border border-theme-border-muted">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-accent-500/10">
                <TrendingUp className="h-5 w-5 text-accent-500" />
              </div>
              <span className="text-sm text-theme-text-secondary">Lifetime Earnings</span>
            </div>
            <p className="text-2xl font-bold text-theme-text">
              {data?.summary.lifetimeEarnings || '$0.00'}
            </p>
          </div>

          {/* Pending */}
          <div className="p-5 rounded-xl bg-theme-surface border border-theme-border-muted">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <span className="text-sm text-theme-text-secondary">Pending Payout</span>
            </div>
            <p className="text-2xl font-bold text-theme-text">
              {data?.summary.pendingEarnings || '$0.00'}
            </p>
            {data?.summary.pendingEarningsCents && data.summary.pendingEarningsCents < (data?.minimumPayoutCents || 3000) && (
              <p className="text-xs text-theme-text-muted mt-1">
                Min. {data?.minimumPayout} for payout
              </p>
            )}
          </div>

          {/* Paid */}
          <div className="p-5 rounded-xl bg-theme-surface border border-theme-border-muted">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
              </div>
              <span className="text-sm text-theme-text-secondary">Total Paid</span>
            </div>
            <p className="text-2xl font-bold text-theme-text">
              {data?.summary.paidEarnings || '$0.00'}
            </p>
          </div>

          {/* App Runs */}
          <div className="p-5 rounded-xl bg-theme-surface border border-theme-border-muted">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Zap className="h-5 w-5 text-purple-500" />
              </div>
              <span className="text-sm text-theme-text-secondary">Total App Runs</span>
            </div>
            <p className="text-2xl font-bold text-theme-text">
              {data?.summary.totalAppRuns?.toLocaleString() || '0'}
            </p>
          </div>
        </div>

        {/* Current Month */}
        {data?.currentMonth && (
          <div className="mb-8 p-6 rounded-xl bg-theme-surface border border-theme-border-muted">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Calendar className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <h2 className="font-semibold text-theme-text">
                    {formatMonth(data.currentMonth.month)}
                  </h2>
                  <p className="text-sm text-theme-text-secondary">Current period</p>
                </div>
              </div>
              <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/20">
                In Progress
              </span>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm text-theme-text-muted">App runs so far</p>
                <p className="text-xl font-semibold text-theme-text">
                  {data.currentMonth.appRuns.toLocaleString()}
                </p>
              </div>
              <div className="h-10 w-px bg-theme-border-muted" />
              <div className="flex-1">
                <p className="text-sm text-theme-text-muted">
                  {data.currentMonth.note || 'Earnings will be calculated at month end'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Earnings History */}
          <div>
            <h2 className="text-lg font-semibold text-theme-text mb-4">Earnings History</h2>
            {data?.history && data.history.length > 0 ? (
              <div className="space-y-3">
                {data.history.map((earning) => (
                  <div 
                    key={earning.id}
                    className="p-4 rounded-xl bg-theme-surface border border-theme-border-muted hover:border-theme-border transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-theme-text">
                        {formatMonth(earning.month)}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(earning.status)}`}>
                        {formatStatus(earning.status)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-theme-text-secondary">
                        <span className="flex items-center gap-1">
                          <Zap className="h-3.5 w-3.5" />
                          {earning.appRuns.toLocaleString()} runs
                        </span>
                        <span>•</span>
                        <span>{earning.sharePercent} share</span>
                      </div>
                      <span className="font-semibold text-theme-text">
                        {earning.earnings}
                      </span>
                    </div>
                    {earning.status === 'paid' && earning.paidAt && (
                      <p className="text-xs text-theme-text-muted mt-2">
                        Paid on {new Date(earning.paidAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 rounded-xl bg-theme-surface border border-theme-border-muted text-center">
                <div className="p-3 rounded-full bg-theme-primary-muted w-fit mx-auto mb-3">
                  <TrendingUp className="h-6 w-6 text-accent-500" />
                </div>
                <h3 className="font-medium text-theme-text mb-1">No earnings yet</h3>
                <p className="text-sm text-theme-text-secondary">
                  Publish your apps to start earning from user activity
                </p>
                <Link to="/dashboard" className="btn btn-primary mt-4">
                  Go to Dashboard
                </Link>
              </div>
            )}
          </div>

          {/* Payouts */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-theme-text">Payout History</h2>
              {isConnected && (
                <button
                  onClick={() => openDashboard()}
                  disabled={connectLoading}
                  className="text-sm text-accent-500 hover:text-accent-400 flex items-center gap-1"
                >
                  Stripe Dashboard
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {data?.payouts && data.payouts.length > 0 ? (
              <div className="space-y-3">
                {data.payouts.map((payout) => (
                  <div 
                    key={payout.id}
                    className="p-4 rounded-xl bg-theme-surface border border-theme-border-muted"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-theme-text">
                        {payout.amount}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(payout.status)}`}>
                        {formatStatus(payout.status)}
                      </span>
                    </div>
                    <p className="text-sm text-theme-text-secondary">
                      {new Date(payout.createdAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                    {payout.errorMessage && (
                      <p className="text-xs text-theme-error mt-2">
                        {payout.errorMessage}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 rounded-xl bg-theme-surface border border-theme-border-muted text-center">
                <div className="p-3 rounded-full bg-emerald-500/10 w-fit mx-auto mb-3">
                  <DollarSign className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-medium text-theme-text mb-1">No payouts yet</h3>
                <p className="text-sm text-theme-text-secondary">
                  {isConnected 
                    ? 'Payouts are processed monthly when you reach the minimum threshold'
                    : 'Connect Stripe to receive payouts'
                  }
                </p>
                {!isConnected && (
                  <button
                    onClick={() => startOnboarding()}
                    disabled={connectLoading}
                    className="btn btn-primary mt-4"
                  >
                    Connect Stripe
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* How it works */}
        <div className="mt-12 p-6 rounded-xl bg-theme-surface border border-theme-border-muted">
          <h2 className="text-lg font-semibold text-theme-text mb-4">How Creator Earnings Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-500/10 flex items-center justify-center text-accent-500 font-semibold text-sm">
                1
              </div>
              <div>
                <h3 className="font-medium text-theme-text mb-1">Users run your apps</h3>
                <p className="text-sm text-theme-text-secondary">
                  Every time someone uses your published mini-app, it counts toward your share.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-500/10 flex items-center justify-center text-accent-500 font-semibold text-sm">
                2
              </div>
              <div>
                <h3 className="font-medium text-theme-text mb-1">Monthly calculation</h3>
                <p className="text-sm text-theme-text-secondary">
                  At month end, 70% of subscription revenue is split among creators based on app usage.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-500/10 flex items-center justify-center text-accent-500 font-semibold text-sm">
                3
              </div>
              <div>
                <h3 className="font-medium text-theme-text mb-1">Automatic payouts</h3>
                <p className="text-sm text-theme-text-secondary">
                  Earnings above $30 are automatically transferred to your connected Stripe account.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
